import { Box, Divider, Paper, Stack, Typography } from '@mui/material';
import type { AuditLogEntry } from '@core/api/auditLog';
import { formatAuditFieldName } from '@core/utils/auditLog';
import AuditLogChangesTable from './AuditLogChangesTable';
import AuditLogValue from './AuditLogValue';

interface AuditLogChangesProps {
  entry: AuditLogEntry;
}

const AuditLogChanges = ({ entry }: AuditLogChangesProps) => {
  const changes = entry.changes ?? {};
  const metadata = Object.entries(entry.metadata ?? {});

  if (!Object.keys(changes).length && !metadata.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        No additional details recorded.
      </Typography>
    );
  }

  const heading = (label: string) => (
    <Typography
      variant="overline"
      color="text.secondary"
      sx={{ display: 'block', mb: 1, letterSpacing: 1 }}
    >
      {label}
    </Typography>
  );

  return (
    <Stack spacing={2}>
      {Object.keys(changes).length > 0 ? (
        <Box>
          {heading('Changes')}
          <AuditLogChangesTable changes={changes} />
        </Box>
      ) : null}
      {metadata.length > 0 ? (
        <Box>
          {heading('Metadata')}
          <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
            <Stack divider={<Divider flexItem />}>
              {metadata.map(([key, value]) => (
                <Box
                  key={key}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: 'minmax(140px, 32%) 1fr',
                    },
                    gap: { xs: 0.5, sm: 2 },
                    px: 2,
                    py: 1.25,
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    fontWeight={600}
                  >
                    {formatAuditFieldName(key)}
                  </Typography>
                  <AuditLogValue value={value} />
                </Box>
              ))}
            </Stack>
          </Paper>
        </Box>
      ) : null}
    </Stack>
  );
};

export default AuditLogChanges;
